import { createContext, useContext, useEffect, useState } from "react";

interface Organization {
    id: number;
    name: string;
}

interface User {
    id: string;
    email: string;
    role?: string;
    app_metadata?: { role?: string };
    [key: string]: any; // allow extra fields from Supabase
}

interface AuthContextType {
    user: User | null;
    email: string | null;
    role: string | null;
    organization: Organization | null;
    token: string | null;
    setUserAndRole: (
        user: User,
        role: string | null,
        organization: Organization | null,
        token: string,
        persist?: "local" | "session"
    ) => void;
    signOut: () => void;
}

const AuthContext = createContext<AuthContextType>({
    user: null,
    role: null,
    email : null,
    organization: null,
    token: null,
    setUserAndRole: () => { },
    signOut: () => { },
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
    const [user, setUser] = useState<User | null>(null);
    const [role, setRole] = useState<string | null>(null);
    const [email, setEmail] = useState<string | null>(null);
    const [organization, setOrganization] = useState<Organization | null>(null);
    const [token, setToken] = useState<string | null>(null);

    // 🔄 Restore token from storage on first load
    useEffect(() => {
        const storedToken =
            localStorage.getItem("token") || sessionStorage.getItem("token");
        if (storedToken) {
            setToken(storedToken);
        }
    }, []);

    const setUserAndRole = (
        newUser: User,
        newRole: string | null,
        newOrg: Organization | null,
        newToken: string,
        persist: "local" | "session" = "local"
    ) => {
        setUser(newUser);
        setRole(newRole);
        setOrganization(newOrg);
        setToken(newToken);

        // Persist depending on "remember me"
        if (persist === "local") {
            localStorage.setItem("token", newToken);
        } else {
            sessionStorage.setItem("token", newToken);
        }
    };

    const signOut = () => {
        setUser(null);
        setRole(null);
        setOrganization(null);
        setToken(null);
        localStorage.removeItem("token");
        sessionStorage.removeItem("token");
        window.location.href = "/login";
    };

    return (
        <AuthContext.Provider
            value={{ user, email, role, organization, token, setUserAndRole, signOut }}
        >
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    return useContext(AuthContext);
}
